<template src="../templates/login.html"></template>

<script>
  import { defineComponent, reactive, ref, onMounted, onUnmounted, nextTick } from 'vue'
  import { useRouter } from 'vue-router'
  import moment from 'moment'
  import CryptoJS from 'crypto-js'
  import { jwtDecode } from 'jwt-decode'

  // XSS Sanitization
  import DOMPurify from 'dompurify'

  // Define background image
  // import bgImage from '../assets/background-kantor.png'
  // import bgImage from '../assets/background.jpg'
  // import bgImage from '../../public/patwal.jpg'
  import bgImage from '/background-kantor.png'
  // import bgImage from '/newBg.jpeg'

  // const router = useRouter()
  // import bgImg1 from '../assets/captcha/bg1.jpg'
  // import bgImg2 from '../assets/captcha/bg2.jpg'
  // import bgImg3 from '../assets/captcha/bg3.jpg'
  // import bgImg4 from '../assets/captcha/bg4.jpg'
  // import bgImg5 from '../assets/captcha/bg5.jpg'

  export default defineComponent({
    name: 'LoginTemplate',
    setup(props, { emit }) {
      const router = useRouter() 
      const form = reactive({
        username   : 'ega',
        password   : 'admin'
      })

      const submitted      = ref(false)
      const showPassword   = ref(false)
      const isLoading      = ref(false)
      const errorMessage   = ref('')
      const successMessage = ref('')

      // Canvas & Captcha Refs
      const bgCanvas    = ref(null)
      const pieceCanvas = ref(null)
      const sliderTrack = ref(null)

      // Captcha Variables
      const sliderLeft = ref(0)
      const isVerified = ref(false)
      let   isDragging = false
      let   startX     = 0
      let   targetX    = 0           
      let   targetY    = 0           
      const pieceSize  = 40          
      const tolerance  = 8           

      // const captchaImages = [bgImg1, bgImg2, bgImg3, bgImg4, bgImg5]
      // function getRandomImage() {
      //     const randomIndex = Math.floor(Math.random() * captchaImages.length)
      //     return captchaImages[randomIndex]
      //   }

      // GEMINI BEGIN HERE  !! ______________________________________________________________________________
      // Draw a classic puzzle shape path
      function createPuzzlePath(ctx, x, y, size) {
          const tabSize = size * 0.25 // Size of the puzzle connector tab
      
          ctx.beginPath()
          ctx.moveTo(x, y)
      
          // Top edge with an outward tab
          ctx.lineTo(x + size * 0.35, y)
          ctx.bezierCurveTo(
                  x + size * 0.35, y - tabSize,
                  x + size * 0.65, y - tabSize,
                  x + size * 0.65, y
          )
          ctx.lineTo(x + size, y)
      
          // Right edge with an inward tab
          ctx.lineTo(x + size, y + size * 0.35)
          ctx.bezierCurveTo(
              x + size + tabSize, y + size * 0.35,
              x + size + tabSize, y + size * 0.65,
              x + size, y + size * 0.65
          )
          ctx.lineTo(x + size, y + size)
      
          // Bottom edge
          ctx.lineTo(x, y + size)
      
          // Left edge
          ctx.lineTo(x, y)
          ctx.closePath()
      }

      function toggleShowPassword() {
        showPassword.value = !showPassword.value
      }

      // Helper: Calculate scale ratio between internal canvas size & displayed CSS size
      function getScaleRatio() {
        if (!bgCanvas.value) return 1
        const rect = bgCanvas.value.getBoundingClientRect()
        return rect.width / bgCanvas.value.width
      }

      // --- Generate Puzzle Piece & Canvas ---
      function initCaptcha() {
          if (!bgCanvas.value || !pieceCanvas.value) return
          
          const bgCtx = bgCanvas.value.getContext('2d')
          const pieceCtx = pieceCanvas.value.getContext('2d')
          
          const width = bgCanvas.value.width
          const height = bgCanvas.value.height
          
          // Reset states
          isVerified.value = false
          sliderLeft.value = 0
          bgCtx.clearRect(0, 0, width, height)
          pieceCtx.clearRect(0, 0, width, height)
          
          // Random target coordinates (leaving margin for tabs)
          targetX = Math.floor(Math.random() * (width - pieceSize - 80)) + 60
          targetY = Math.floor(Math.random() * (height - pieceSize - 30)) + 20
          
          const img = new Image()
          // Use this from fixed asset background 
          // img.src = getRandomImage()
          img.crossOrigin = 'Anonymous'
          img.src = 'https://picsum.photos/332/100?random=' + Math.random()
          
          // Gemini Vibe ________________________________________________
          img.onload = () => {
              // 1. Draw base image on background canvas
              bgCtx.drawImage(img, 0, 0, width, height)
            
              // 2. Draw puzzle piece on pieceCanvas shifted to 0px
              pieceCtx.save()
              // Path drawn at position 0, targetY instead of targetX, targetY
              createPuzzlePath(pieceCtx, 0, targetY, pieceSize)
              pieceCtx.clip() // Clip drawing area to puzzle path shape
              
              // Draw image shifted back by -targetX so position 0 displays targetX content
              pieceCtx.drawImage(img, -targetX, 0, width, height)
              pieceCtx.restore()
            
              // Add border stroke & shadow to the piece at position 0
              pieceCtx.save()
              createPuzzlePath(pieceCtx, 0, targetY, pieceSize)
              pieceCtx.strokeStyle = '#ffffff'
              pieceCtx.lineWidth = 2
              pieceCtx.shadowColor = 'rgba(0, 0, 0, 0.5)'
              pieceCtx.shadowBlur = 4
              pieceCtx.stroke()
              pieceCtx.restore()
            
              // 3. Draw dark cutout hole on background canvas at targetX
              bgCtx.save()
              createPuzzlePath(bgCtx, targetX, targetY, pieceSize)
              bgCtx.fillStyle = 'rgba(0, 0, 0, 0.7)'
              bgCtx.fill()
              bgCtx.strokeStyle = '#ffffff'
              bgCtx.lineWidth = 1.5
              bgCtx.stroke()
              bgCtx.restore()
            }
      }

      function resetCaptcha() {
        initCaptcha()
      }

      // --- Drag Mechanics ---
      function startDrag(event) {
        if (isVerified.value || isLoading.value) return
        isDragging = true
        startX = event.type.includes('touch') ? event.touches[0].clientX : event.clientX

        window.addEventListener('mousemove', onDrag)
        window.addEventListener('touchmove', onDrag)
        window.addEventListener('mouseup', stopDrag)
        window.addEventListener('touchend', stopDrag)
      }

      function onDrag(event) {
        if (!isDragging || isVerified.value) return

        const currentX = event.type.includes('touch') ? event.touches[0].clientX : event.clientX
        const deltaX = currentX - startX
        const maxTrackWidth = sliderTrack.value ? sliderTrack.value.clientWidth - 40 : 280

        if (deltaX <= 0) {
          sliderLeft.value = 0
        } else if (deltaX >= maxTrackWidth) {
          sliderLeft.value = maxTrackWidth
        } else {
          sliderLeft.value = deltaX
        }
      }

      function stopDrag() {
        if (!isDragging) return
        isDragging = false

        window.removeEventListener('mousemove', onDrag)
        window.removeEventListener('touchmove', onDrag)
        window.removeEventListener('mouseup', stopDrag)
        window.removeEventListener('touchend', stopDrag)

        // Convert targetX to match current CSS screen pixels using scale ratio
        const scaleRatio = getScaleRatio()
        const scaledTargetX = targetX * scaleRatio

        // Calculate accuracy match against scaled target
        const difference = Math.abs(sliderLeft.value - scaledTargetX)

        if (difference <= tolerance) {
          isVerified.value = true
          sliderLeft.value = scaledTargetX // Snap perfectly into place
        } else {
          // Failed attempt: snap back and refresh captcha
          sliderLeft.value = 0
          initCaptcha()
        }
      }

      function sanitizeInput(val) {
        if (typeof val === 'string') {
            return DOMPurify.sanitize(val.trim())
        }
        return val
      }      

      onMounted(() => {
        nextTick(() => {
          initCaptcha()
        })
      })

      onUnmounted(() => {
        stopDrag()
      })
      // GEMINI STOP HERE  !! ______________________________________________________________________________

      async function handleLogin() {
        errorMessage.value = ''
        successMessage.value = ''

        if (!form.username || !form.password) {
          errorMessage.value = 'Please fill in all fields.'
          return
        }

        if (!isVerified.value) {
          errorMessage.value = 'Please complete the security verification.'
          return
        }

        isLoading.value = true

        const payload = new URLSearchParams()

        // Check XSS here __________________________
        payload.append('username'   , sanitizeInput( form.username )?? "")
        payload.append('password'   , sanitizeInput( form.password )?? "")
        // payload.append('timeStamp'  , sanitizeInput( moment().add(-7, 'hours').format("YYYY-MM-DD HH:mm:ss"))?? "" )

        try {
          
          // Try to HIDE THIS REQUEST ____________________
          const SECRET_KEY       = import.meta.env.VITE_APP_SECRET
          const SECRET_URL       = import.meta.env.VITE_URL_SECRET
          const endpoint         = "/api/auth/login"
          const rawPayload       = payload.toString()
          const encryptedTarget  = CryptoJS.AES.encrypt( endpoint , SECRET_URL).toString();
          const encryptedUrl     = CryptoJS.AES.encrypt( endpoint , SECRET_KEY).toString();
          const encryptedPayload = CryptoJS.AES.encrypt(rawPayload, SECRET_KEY).toString();

          // Base64 encrypted Url to avoid "/" character, which is not allowed in URL
          // const targeturl        =  '/api/4.0.4/' + btoa(encodeURIComponent(encryptedTarget)) 
          const baseUrl          =  import.meta.env.VITE_PROXY_URL
          
          // Try to SECRET LOGIN REQUEST _______________________
          const response = await fetch(`${baseUrl}/secure-proxy`, {
            method : 'POST',                                    // Force POST so params stay hidden in the body
            headers: { 'Content-Type' : 'application/json' },
            body   : JSON.stringify({
                      t: encryptedUrl
                    , m: "POST"
                    , p: encryptedPayload
                  })
            });
          
          const api_resp = await response.json()

          if (response.ok && api_resp.success) {
              const token = api_resp.data?.token
          
              if (token) {
                  const decoded = jwtDecode(token)
                  // decoded.exp is in seconds, convert to milliseconds
                  const expiresAt = decoded.exp * 1000
                  
                  // Save all info into locale storage, CAN'T save object !! must be convert into string !!!
                  localStorage.setItem('auth_token'         , token)
                  localStorage.setItem('user_profile'       , JSON.stringify(api_resp.data.UserResult))
                  localStorage.setItem('session_expires_at' , expiresAt.toString())

              }
              
              successMessage.value = api_resp.message || 'Login success'
              emit('login-success', { username: form.username, token })
              submitted.value = true
              try {
                  await router.push('/dashboard')
              } catch (err) {
                  console.error('Navigation error:', err)
              }
          } else {
              errorMessage.value = api_resp.message || 'Login failed.'
              initCaptcha()
          }
        } catch (error) {
          console.error('API Error:', error)
          errorMessage.value = 'Network error or unable to reach the server.'
        } finally {
          isLoading.value = false
        }
      }

      return {
         form
        ,submitted
        ,showPassword
        ,toggleShowPassword
        ,handleLogin
        ,isLoading
        ,errorMessage
        ,successMessage
        ,isVerified
        ,sliderLeft
        ,sliderTrack
        ,bgCanvas
        ,pieceCanvas
        ,startDrag
        ,resetCaptcha
        ,createPuzzlePath
        ,bgImage
      }
    }
  })
</script>